import {
  getPermission,
  READ,
  CREATE,
  EDIT,
  DELETE,
  ADMIN,
  ACCOUNTCODEPLAN,
  ACCOUNTINGMAPPING,
  CLEARING,
  MONITORING,
  REGISTER,
  REPORTS,
  SETTLEMENTS,
} from 'permissions';

export interface AccessRule {
  permissions?: string | string[];
  internalRequired?: boolean;
}

export interface AccessContext {
  hasPrivilege: (privileges: string | string[]) => boolean;
  internal: boolean;
}

export const canAccess = (rule: AccessRule, { hasPrivilege, internal }: AccessContext) =>
  (!rule.internalRequired || internal) && (!rule.permissions || hasPrivilege(rule.permissions));

/**
 * Access rules for every guarded route; menu items reuse them so the menu and the routes cannot drift apart.
 * `internalRequired` also mirrors backend endpoints still guarded by `authorizeEntur` in cleos-services.
 */
export const routeAccess = {
  agreements: { permissions: getPermission(CLEARING, READ) },
  clearingCreate: { permissions: getPermission(CLEARING, CREATE) },
  clearingMappings: { permissions: getPermission(CLEARING, READ) },
  accountingMappings: { permissions: [getPermission(CLEARING, READ), getPermission(ACCOUNTINGMAPPING, READ)] },
  accountingMappingsCreate: {
    permissions: [getPermission(CLEARING, READ), getPermission(ACCOUNTINGMAPPING, CREATE)],
  },
  accountingRules: { permissions: getPermission(CLEARING, READ) },
  settledData: { permissions: getPermission(CLEARING, READ), internalRequired: true },

  settlements: { permissions: getPermission(SETTLEMENTS, READ), internalRequired: true },
  settlementsCreate: { permissions: getPermission(SETTLEMENTS, CREATE), internalRequired: true },
  settlementsEdit: { permissions: getPermission(SETTLEMENTS, EDIT), internalRequired: true },

  reportSearch: { permissions: getPermission(REPORTS, READ) },
  reportTemplates: { permissions: getPermission(REPORTS, READ), internalRequired: true },
  reportTemplateUpdate: { permissions: getPermission(REPORTS, EDIT), internalRequired: true },
  reportProduction: { permissions: getPermission(REPORTS, CREATE), internalRequired: true },
  reportStatistics: { permissions: getPermission(REPORTS, READ), internalRequired: true },
  failedReports: { permissions: [getPermission(REPORTS, READ), getPermission(REPORTS, EDIT)] },

  register: { permissions: getPermission(REGISTER, READ), internalRequired: true },
  registerCreate: { permissions: getPermission(REGISTER, CREATE), internalRequired: true },
  lines: { permissions: getPermission(REGISTER, READ) },
  // cleos-services guards creating and editing lines with ADMIN.DELETE, not REGISTER.
  linesWrite: { permissions: getPermission(ADMIN, DELETE) },
  accountCodePlan: { permissions: getPermission(ACCOUNTCODEPLAN, READ) },
  accountCodePlanCreate: { permissions: getPermission(ACCOUNTCODEPLAN, CREATE) },
  admin: { permissions: getPermission(ADMIN, READ), internalRequired: true },
  adminCreate: { permissions: getPermission(ADMIN, CREATE), internalRequired: true },
  // Creating charts of accounts, partners, transaction types and data products requires DELETE by convention.
  adminCreateRestricted: { permissions: getPermission(ADMIN, DELETE), internalRequired: true },
  monitoring: { permissions: getPermission(MONITORING, READ) },
} satisfies Record<string, AccessRule>;
